/**
 * Chart export — composes the live recharts plot into a chart that looks
 * exactly like the dz-publishing-process chart engine's output
 * (createChart.js / createLegend.js / export_png.py / export_svg.py):
 *
 *   #F2F0F0 card (8px radius, 25px padding) on white
 *   ├─ title      GT Alpina 30px black, lh 1.15, ls -0.02em, mb 8
 *   ├─ subtitle   ABC Social 16px #7E7E7E, lh 1.2, mb 30
 *   ├─ plot       the cloned recharts SVG (flush-left y labels)
 *   ├─ legend     white pill chips, 16px below the plot, left-aligned
 *   └─ footer     20px below: source 11px black left · logo 150px right
 *
 * PNG renders at 2x (engine device_scale). Fonts are embedded as base64
 * @font-face so both the standalone SVG and the isolated <img> used for
 * PNG rasterization render GT Alpina / ABC Social instead of fallbacks.
 */

// The official DZ wordmark (same file as the chart engine's
// references/corporate-design-dz/corporate-brand-dz.svg). Inlined at build
// time so it travels inside the UMD/ES bundle — a runtime fetch would resolve
// against the *host page's* origin root, where the file doesn't exist, and the
// wordmark would silently drop out of the export.
import brandLogoSvg from '../assets/corporate-brand-dz.svg?raw';
import { t } from '../i18n';

export interface LegendItem {
  name: string;
  color: string;
  dashed?: boolean;
}

export interface ChartExportOptions {
  title?: string;
  breadcrumb?: string[];
  filename?: string;
  legend?: LegendItem[];
  filterInfo?: string;
  /** Y-axis unit caption (e.g. "Mrd. €"), drawn above the plot like the widget's AxisUnitLabel. */
  axisUnit?: string;
  /** X of the axis line within the plot (= YAxis width); the caption anchors there. */
  axisUnitX?: number;
}

// ── Engine card geometry (createChart.js) ────────────────────────────────
const CARD_PADDING = 25;
const CARD_BG = '#F2F0F0';
const CARD_RADIUS = 8;

const TITLE_SIZE = 30;
const TITLE_LINE_HEIGHT = 1.15;
const TITLE_LETTER_SPACING = -0.02 * TITLE_SIZE; // -0.02em in px
const TITLE_MARGIN_BOTTOM = 8;

const SUBTITLE_SIZE = 16;
const SUBTITLE_LINE_HEIGHT = 1.2;
const SUBTITLE_COLOR = '#7E7E7E';
const SUBTITLE_MARGIN_BOTTOM = 30;

// ── Y-axis unit caption (widget AxisUnitLabel: 13px sans black, 2px gap) ──
const AXIS_UNIT_SIZE = 13;
const AXIS_UNIT_LINE_HEIGHT = 1.2;
const AXIS_UNIT_MARGIN_BOTTOM = 2;

// ── Engine legend chips (createLegend.js, Figma layout_LZS74O) ───────────
const CHIP_HEIGHT = 26;
const CHIP_PADDING_X = 9;
const CHIP_RADIUS = 19;
const CHIP_GAP = 6; // dot → label
const DOT_SIZE = 10;
const ITEM_GAP = 8; // chip → chip
const ROW_GAP = 8; // row → row
const LEGEND_GAP = 16; // plot → legend
const LABEL_SIZE = 13;

// ── Engine footer (.bottom-container) ────────────────────────────────────
const FOOTER_GAP = 20; // legend → footer
const SOURCE_SIZE = 11;
// Source credit line: i18n common.source (shared with the embed footer),
// read at export time so it follows the widget language.
const LOGO_WIDTH = 150;

// Page font family names (dz-shared-styles fonts.scss). Canvas/SVG need
// literal stacks — CSS var() is not supported there.
const SERIF_FAMILY = '"GT-Alpina", ui-serif, Georgia, serif';
const SANS_FAMILY = '"ABC-Social", "Helvetica Neue", Helvetica, Arial, sans-serif';

const SVG_NS = 'http://www.w3.org/2000/svg';

// ─────────────────────────────────────────────────────────────────────────
// Brand logo (cached)
// ─────────────────────────────────────────────────────────────────────────

let brandLogoCache: { dataUrl: string; width: number; height: number } | null = null;
let brandLogoRasterCache: { dataUrl: string; width: number; height: number } | null = null;

/**
 * The build-time-inlined SVG wordmark as a vector data URI — used for the SVG
 * export, where it stays crisp at any scale. width/height are the viewBox
 * units (only the aspect ratio matters).
 */
function loadBrandLogo(): { dataUrl: string; width: number; height: number } {
  if (brandLogoCache) return brandLogoCache;

  const svgText = brandLogoSvg;
  if (!svgText.includes('<svg')) throw new Error('Brand logo asset is not an SVG');

  const viewBox = /viewBox=["']\s*[\d.+-]+[\s,]+[\d.+-]+[\s,]+([\d.+-]+)[\s,]+([\d.+-]+)/.exec(svgText);
  const width = viewBox ? parseFloat(viewBox[1]) : 515;
  const height = viewBox ? parseFloat(viewBox[2]) : 218;

  // btoa is latin1-only; round-trip through UTF-8 percent-encoding.
  const base64 = btoa(unescape(encodeURIComponent(svgText)));
  brandLogoCache = { dataUrl: `data:image/svg+xml;base64,${base64}`, width, height };
  return brandLogoCache;
}

/**
 * A rasterized (PNG) variant of the wordmark for the PNG export. The PNG path
 * rasterizes the whole card by loading the serialized SVG as an <img> and
 * drawing it to a canvas — and Chromium/Edge refuse to paint an SVG <image>
 * that is *itself* an SVG (nested SVG). A standalone SVG → <img> → canvas is
 * fine, so we pre-bake the wordmark to a PNG data URI here; embedding that
 * (raster) into the card's <image> rasterizes reliably. Rendered at 2x the
 * on-card width to stay sharp at the export's 2x device scale.
 */
async function loadBrandLogoRaster(): Promise<{ dataUrl: string; width: number; height: number }> {
  if (brandLogoRasterCache) return brandLogoRasterCache;

  const vector = loadBrandLogo();
  const scale = 2;
  const w = Math.round(LOGO_WIDTH * scale);
  const h = Math.round((LOGO_WIDTH / vector.width) * vector.height * scale);

  const pngDataUrl = await new Promise<string>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Failed to get canvas context for logo raster'));
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject(new Error('Failed to rasterize brand logo'));
    img.src = vector.dataUrl;
  });

  brandLogoRasterCache = { dataUrl: pngDataUrl, width: vector.width, height: vector.height };
  return brandLogoRasterCache;
}

// ─────────────────────────────────────────────────────────────────────────
// Font embedding (cached) — engine export_svg.py approach: @font-face with
// base64 data URIs inside <defs><style>. The host page already declares
// ABC-Social / GT-Alpina via @font-face (dz-shared-styles); we locate those
// rules in the CSSOM, fetch the font binaries and inline them.
// ─────────────────────────────────────────────────────────────────────────

const EXPORT_FONT_FAMILIES = ['ABC-Social', 'GT-Alpina'];

let fontFaceCssCache: string | null = null;

function fontFormatForUrl(url: string): { format: string; mime: string } {
  if (url.includes('.woff2')) return { format: 'woff2', mime: 'font/woff2' };
  if (url.includes('.woff')) return { format: 'woff', mime: 'font/woff' };
  if (url.includes('.otf')) return { format: 'opentype', mime: 'font/otf' };
  if (url.includes('.ttf')) return { format: 'truetype', mime: 'font/ttf' };
  return { format: 'woff', mime: 'font/woff' };
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunk = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/**
 * Collect the page's @font-face rules for the export families. Cross-origin
 * stylesheets throw on cssRules access — skipped via try/catch.
 */
function findFontFaceRules(): { family: string; weight: string; style: string; url: string }[] {
  const found: { family: string; weight: string; style: string; url: string }[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList;
    try {
      rules = sheet.cssRules;
    } catch {
      continue; // cross-origin stylesheet
    }
    for (const rule of Array.from(rules)) {
      if (!(rule instanceof CSSFontFaceRule)) continue;
      const family = rule.style.getPropertyValue('font-family').replace(/["']/g, '').trim();
      if (!EXPORT_FONT_FAMILIES.includes(family)) continue;
      const src = rule.style.getPropertyValue('src');
      const match = /url\(["']?([^"')]+)["']?\)/.exec(src);
      if (!match) continue;
      // Resolve relative URLs against the owning stylesheet, not the page.
      const base = rule.parentStyleSheet?.href || document.baseURI;
      let url: string;
      try {
        url = new URL(match[1], base).href;
      } catch {
        continue;
      }
      found.push({
        family,
        weight: rule.style.getPropertyValue('font-weight') || 'normal',
        style: rule.style.getPropertyValue('font-style') || 'normal',
        url,
      });
    }
  }
  return found;
}

/**
 * A real font binary starts with one of these magics. Dev servers answer
 * unknown paths with an HTML SPA fallback (status 200), so without this
 * check we would happily embed index.html as "font" data.
 */
function looksLikeFont(buffer: ArrayBuffer): boolean {
  if (buffer.byteLength < 4) return false;
  const b = new Uint8Array(buffer, 0, 4);
  const tag = String.fromCharCode(b[0], b[1], b[2], b[3]);
  if (tag === 'wOFF' || tag === 'wOF2' || tag === 'OTTO' || tag === 'true') return true;
  return b[0] === 0 && b[1] === 1 && b[2] === 0 && b[3] === 0; // TTF sfnt
}

/**
 * Build a CSS block of base64 @font-face rules for ABC-Social + GT-Alpina.
 * Returns '' when nothing could be embedded — exports then fall back to
 * the Helvetica/Georgia stacks rather than failing.
 */
async function loadExportFontsCss(): Promise<string> {
  if (fontFaceCssCache !== null) return fontFaceCssCache;

  const rules = findFontFaceRules();
  const cssParts: string[] = [];
  const embedded = new Set<string>(); // one face per family/weight/style
  // Sequential on purpose: faces can be declared more than once (e.g. shared
  // stylesheet + dev fallback) and only the first WORKING source should win.
  for (const rule of rules) {
    const key = `${rule.family}|${rule.weight}|${rule.style}`;
    if (embedded.has(key)) continue;
    try {
      const response = await fetch(rule.url, { mode: 'cors' });
      if (!response.ok) continue;
      const buffer = await response.arrayBuffer();
      if (!looksLikeFont(buffer)) continue;
      const { format, mime } = fontFormatForUrl(rule.url);
      cssParts.push(
        `@font-face{font-family:'${rule.family}';` +
          `src:url('data:${mime};base64,${arrayBufferToBase64(buffer)}') format('${format}');` +
          `font-weight:${rule.weight};font-style:${rule.style};}`
      );
      embedded.add(key);
    } catch (error) {
      console.warn(`Chart export: could not embed font ${rule.url}`, error);
    }
  }

  fontFaceCssCache = cssParts.join('\n');
  return fontFaceCssCache;
}

// ─────────────────────────────────────────────────────────────────────────
// Clone preparation
// ─────────────────────────────────────────────────────────────────────────

/**
 * Inlines computed styles onto SVG elements for export.
 * Recharts uses CSS classes, so we need to copy computed styles inline.
 */
function inlineStyles(svgElement: SVGElement): SVGElement {
  const clone = svgElement.cloneNode(true) as SVGElement;

  const originalElements = svgElement.querySelectorAll('*');
  const clonedElements = clone.querySelectorAll('*');

  const styleProperties = [
    'fill',
    'stroke',
    'stroke-width',
    'stroke-dasharray',
    'stroke-linecap',
    'stroke-linejoin',
    'opacity',
    'fill-opacity',
    'stroke-opacity',
    'font-family',
    'font-size',
    'font-weight',
    'text-anchor',
    'dominant-baseline',
  ];

  originalElements.forEach((originalEl, index) => {
    const clonedEl = clonedElements[index] as SVGElement;
    if (!clonedEl) return;
    const computedStyle = window.getComputedStyle(originalEl);
    styleProperties.forEach(prop => {
      const value = computedStyle.getPropertyValue(prop);
      if (value && value !== 'none' && value !== '') {
        clonedEl.style.setProperty(prop, value);
      }
    });
  });

  const rootComputedStyle = window.getComputedStyle(svgElement);
  styleProperties.forEach(prop => {
    const value = rootComputedStyle.getPropertyValue(prop);
    if (value && value !== 'none' && value !== '') {
      clone.style.setProperty(prop, value);
    }
  });

  return clone;
}

/**
 * Strip transient interaction state from the clone so the export shows the
 * chart's resting look: no tooltip cursor, no active dots, no hover dimming.
 * The dashed Haushaltstechnik shape (stroke-dasharray) keeps its deliberate
 * low fill-opacity; everything else is reset to fully opaque.
 */
function sanitizeClone(clone: SVGElement): void {
  clone
    .querySelectorAll('.recharts-tooltip-cursor, .recharts-active-dot')
    .forEach(el => el.remove());

  clone
    .querySelectorAll<SVGElement>(
      '.recharts-area-area, .recharts-rectangle, .recharts-bar-rectangle path'
    )
    .forEach(el => {
      const dash = el.style.getPropertyValue('stroke-dasharray');
      if (dash && dash !== 'none') return; // Haushaltstechnik exception
      const fillOpacity = parseFloat(el.style.getPropertyValue('fill-opacity') || '1');
      if (fillOpacity < 1) el.style.setProperty('fill-opacity', '1');
    });
}

// ─────────────────────────────────────────────────────────────────────────
// Text measurement / wrapping
// ─────────────────────────────────────────────────────────────────────────

let measureCtx: CanvasRenderingContext2D | null = null;

function getMeasureCtx(): CanvasRenderingContext2D {
  if (!measureCtx) {
    measureCtx = document.createElement('canvas').getContext('2d')!;
  }
  return measureCtx;
}

function measureText(text: string, font: string, letterSpacingPx = 0): number {
  const ctx = getMeasureCtx();
  ctx.font = font;
  const base = ctx.measureText(text).width;
  // Canvas letterSpacing support is inconsistent; approximate manually.
  return base + letterSpacingPx * Math.max(0, text.length - 1);
}

function wrapText(text: string, font: string, maxWidth: number, letterSpacingPx = 0): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const lines: string[] = [];
  let line = words[0];
  for (const word of words.slice(1)) {
    const candidate = `${line} ${word}`;
    if (measureText(candidate, font, letterSpacingPx) > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  lines.push(line);
  return lines;
}

// ─────────────────────────────────────────────────────────────────────────
// SVG element helpers
// ─────────────────────────────────────────────────────────────────────────

function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number>
): SVGElementTagNameMap[K] {
  const el = document.createElementNS(SVG_NS, tag);
  for (const [key, value] of Object.entries(attrs)) {
    el.setAttribute(key, String(value));
  }
  return el;
}

interface TextBlockStyle {
  size: number;
  lineHeight: number;
  family: string;
  fill: string;
  letterSpacing?: number;
}

/**
 * Append a wrapped text block at (x, yCursor); returns the y cursor after
 * the block. Baselines sit at ~0.8 * fontSize within each line box.
 */
function addTextBlock(
  parent: SVGElement,
  text: string,
  x: number,
  yCursor: number,
  maxWidth: number,
  style: TextBlockStyle
): number {
  const font = `${style.size}px ${style.family}`;
  const lines = wrapText(text, font, maxWidth, style.letterSpacing ?? 0);
  const lineBox = style.size * style.lineHeight;
  lines.forEach((line, i) => {
    const textEl = svgEl('text', {
      x,
      y: yCursor + i * lineBox + style.size * 0.8,
      'font-family': style.family,
      'font-size': style.size,
      'font-weight': 400,
      fill: style.fill,
    });
    if (style.letterSpacing) textEl.setAttribute('letter-spacing', String(style.letterSpacing));
    textEl.textContent = line;
    parent.appendChild(textEl);
  });
  return yCursor + lines.length * lineBox;
}

// ─────────────────────────────────────────────────────────────────────────
// Legend layout (engine pill chips)
// ─────────────────────────────────────────────────────────────────────────

interface ChipRow {
  items: { item: LegendItem; chipWidth: number }[];
}

function layoutLegend(legend: LegendItem[], maxWidth: number): { rows: ChipRow[]; height: number } {
  const font = `${LABEL_SIZE}px ${SANS_FAMILY}`;
  const rows: ChipRow[] = [{ items: [] }];
  let rowWidth = 0;
  legend.forEach(item => {
    const chipWidth =
      CHIP_PADDING_X + DOT_SIZE + CHIP_GAP + measureText(item.name, font) + CHIP_PADDING_X;
    const advance = chipWidth + (rows[rows.length - 1].items.length > 0 ? ITEM_GAP : 0);
    if (rowWidth + advance > maxWidth && rows[rows.length - 1].items.length > 0) {
      rows.push({ items: [] });
      rowWidth = 0;
    }
    rows[rows.length - 1].items.push({ item, chipWidth });
    rowWidth += chipWidth + (rows[rows.length - 1].items.length > 1 ? ITEM_GAP : 0);
  });
  const rowCount = rows[0].items.length === 0 ? 0 : rows.length;
  return {
    rows,
    height: rowCount === 0 ? 0 : rowCount * CHIP_HEIGHT + (rowCount - 1) * ROW_GAP,
  };
}

function drawLegend(parent: SVGElement, rows: ChipRow[], x: number, yCursor: number): number {
  let y = yCursor;
  rows.forEach(row => {
    let chipX = x;
    row.items.forEach(({ item, chipWidth }) => {
      // Pill chip background (white, 19px radius)
      parent.appendChild(
        svgEl('rect', {
          x: chipX,
          y,
          width: chipWidth,
          height: CHIP_HEIGHT,
          rx: CHIP_RADIUS,
          ry: CHIP_RADIUS,
          fill: '#FFFFFF',
        })
      );
      // Colored dot — dashed = Haushaltstechnik (black dashed outline, no fill)
      const dot = svgEl('circle', {
        cx: chipX + CHIP_PADDING_X + DOT_SIZE / 2,
        cy: y + CHIP_HEIGHT / 2,
        r: DOT_SIZE / 2,
      });
      if (item.dashed) {
        dot.setAttribute('fill', 'none');
        dot.setAttribute('stroke', '#000000');
        dot.setAttribute('stroke-width', '1');
        dot.setAttribute('stroke-dasharray', '2 2');
      } else {
        dot.setAttribute('fill', item.color);
      }
      parent.appendChild(dot);
      // Label
      const label = svgEl('text', {
        x: chipX + CHIP_PADDING_X + DOT_SIZE + CHIP_GAP,
        y: y + CHIP_HEIGHT / 2,
        dy: '0.35em',
        'font-family': SANS_FAMILY,
        'font-size': LABEL_SIZE,
        'font-weight': 400,
        fill: '#000000',
      });
      label.textContent = item.name;
      parent.appendChild(label);

      chipX += chipWidth + ITEM_GAP;
    });
    y += CHIP_HEIGHT + ROW_GAP;
  });
  return yCursor + rows.length * CHIP_HEIGHT + (rows.length - 1) * ROW_GAP;
}

// ─────────────────────────────────────────────────────────────────────────
// Card composition
// ─────────────────────────────────────────────────────────────────────────

/**
 * Compose the engine-style chart card around the prepared plot clone.
 */
function composeEngineCard(
  plotClone: SVGElement,
  plotWidth: number,
  plotHeight: number,
  options: ChartExportOptions,
  fontCss: string,
  logo: { dataUrl: string; width: number; height: number } | null
): SVGElement {
  // Title = deepest breadcrumb level; subtitle = ONLY the root-first parent
  // path joined with "›" — no descriptive text like "Unterkategorien" —
  // plus the active filter ("Gefiltert nach: …") when one is set
  // (user decisions 2026-06-02). Level 1 unfiltered → no subtitle line.
  const crumbs = options.breadcrumb?.length
    ? options.breadcrumb
    : options.title
      ? [options.title]
      : [];
  const titleText = crumbs[crumbs.length - 1] ?? '';
  const subtitleText = [crumbs.slice(0, -1).join(' › '), options.filterInfo]
    .filter(Boolean)
    .join(' · ');

  const contentX = CARD_PADDING;
  const contentWidth = plotWidth;
  const cardWidth = plotWidth + 2 * CARD_PADDING;

  const svg = svgEl('svg', { xmlns: SVG_NS, 'xmlns:xlink': 'http://www.w3.org/1999/xlink' });

  if (fontCss) {
    const defs = svgEl('defs', {});
    const style = svgEl('style', {});
    style.textContent = fontCss;
    defs.appendChild(style);
    svg.appendChild(defs);
  }

  // Backgrounds appended now, sized at the end (height is content-driven).
  const pageBg = svgEl('rect', { width: '100%', height: '100%', fill: '#FFFFFF' });
  const cardBg = svgEl('rect', {
    x: 0,
    y: 0,
    width: cardWidth,
    height: 0,
    rx: CARD_RADIUS,
    ry: CARD_RADIUS,
    fill: CARD_BG,
  });
  svg.appendChild(pageBg);
  svg.appendChild(cardBg);

  let y = CARD_PADDING;

  // Title (GT Alpina 30px black) + 8px gap
  if (titleText) {
    y = addTextBlock(svg, titleText, contentX, y, contentWidth, {
      size: TITLE_SIZE,
      lineHeight: TITLE_LINE_HEIGHT,
      family: SERIF_FAMILY,
      fill: '#000000',
      letterSpacing: TITLE_LETTER_SPACING,
    });
    y += TITLE_MARGIN_BOTTOM;
  }

  // Subtitle (ABC Social 16px grey) + 30px gap
  if (subtitleText) {
    y = addTextBlock(svg, subtitleText, contentX, y, contentWidth, {
      size: SUBTITLE_SIZE,
      lineHeight: SUBTITLE_LINE_HEIGHT,
      family: SANS_FAMILY,
      fill: SUBTITLE_COLOR,
    });
  }
  y += SUBTITLE_MARGIN_BOTTOM;

  // Y-axis unit caption — same as the widget's AxisUnitLabel: ABC Social
  // 13px black, anchored at the axis line, 2px above the plot.
  if (options.axisUnit) {
    const unit = svgEl('text', {
      x: contentX + (options.axisUnitX ?? 0),
      y: y + AXIS_UNIT_SIZE * 0.8,
      'font-family': SANS_FAMILY,
      'font-size': AXIS_UNIT_SIZE,
      'font-weight': 400,
      fill: '#000000',
    });
    unit.textContent = options.axisUnit;
    svg.appendChild(unit);
    y += AXIS_UNIT_SIZE * AXIS_UNIT_LINE_HEIGHT + AXIS_UNIT_MARGIN_BOTTOM;
  }

  // Plot
  const plotGroup = svgEl('g', { transform: `translate(${contentX}, ${y})` });
  Array.from(plotClone.childNodes).forEach(child => {
    plotGroup.appendChild(child.cloneNode(true));
  });
  svg.appendChild(plotGroup);
  y += plotHeight;

  // Legend (pill chips, 16px below the plot, flush-left with the title)
  if (options.legend && options.legend.length > 0) {
    const layout = layoutLegend(options.legend, contentWidth);
    if (layout.height > 0) {
      y += LEGEND_GAP;
      y = drawLegend(svg, layout.rows, contentX, y);
    }
  }

  // Footer: source line bottom-left, logo bottom-right (flex-end)
  y += FOOTER_GAP;
  const logoHeight = logo ? (LOGO_WIDTH / logo.width) * logo.height : 0;
  const sourceText = t().common.source;
  const footerHeight = Math.max(logoHeight, SOURCE_SIZE * 1.2);

  const source = svgEl('text', {
    x: contentX,
    y: y + footerHeight - SOURCE_SIZE * 0.2, // baseline at the footer bottom
    'font-family': SANS_FAMILY,
    'font-size': SOURCE_SIZE,
    'font-weight': 400,
    fill: '#000000',
  });
  source.textContent = sourceText;
  svg.appendChild(source);

  if (logo) {
    const image = svgEl('image', {
      x: contentX + contentWidth - LOGO_WIDTH,
      y: y + footerHeight - logoHeight,
      width: LOGO_WIDTH,
      height: logoHeight,
      href: logo.dataUrl,
    });
    image.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', logo.dataUrl);
    svg.appendChild(image);
  }
  y += footerHeight;

  const cardHeight = y + CARD_PADDING;
  cardBg.setAttribute('height', String(cardHeight));
  svg.setAttribute('width', String(cardWidth));
  svg.setAttribute('height', String(cardHeight));
  svg.setAttribute('viewBox', `0 0 ${cardWidth} ${cardHeight}`);

  return svg;
}

/**
 * Shared preparation for both export formats: clone + sanitize the plot,
 * load fonts + logo, compose the engine card.
 */
async function prepareExportSvg(
  svgElement: SVGElement,
  options: ChartExportOptions,
  rasterLogo = false
): Promise<{ svg: SVGElement; width: number; height: number }> {
  const plotClone = inlineStyles(svgElement);
  sanitizeClone(plotClone);

  // The legend lives outside the recharts SVG, so the SVG's own box is the
  // plot: ResponsiveContainer width × fixed height (400 area / 300 bar).
  const plotWidth =
    svgElement.clientWidth || parseInt(svgElement.getAttribute('width') || '800', 10);
  const plotHeight =
    svgElement.clientHeight || parseInt(svgElement.getAttribute('height') || '400', 10);

  const fontCss = await loadExportFontsCss();

  // The PNG path rasterizes the card via <img>+canvas, which won't paint a
  // nested SVG <image>; use the pre-rasterized (PNG) wordmark there instead.
  let logo: { dataUrl: string; width: number; height: number } | null = null;
  try {
    logo = rasterLogo ? await loadBrandLogoRaster() : loadBrandLogo();
  } catch (error) {
    console.warn('Chart export: brand logo unavailable', error);
  }

  const svg = composeEngineCard(plotClone, plotWidth, plotHeight, options, fontCss, logo);
  return {
    svg,
    width: parseInt(svg.getAttribute('width')!, 10),
    height: parseInt(svg.getAttribute('height')!, 10),
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Serialization / download
// ─────────────────────────────────────────────────────────────────────────

function serializeSvg(svgElement: SVGElement): string {
  const serializer = new XMLSerializer();
  let svgString = serializer.serializeToString(svgElement);
  if (!svgString.startsWith('<?xml')) {
    svgString = '<?xml version="1.0" encoding="UTF-8"?>\n' + svgString;
  }
  if (!svgString.includes('xmlns="http://www.w3.org/2000/svg"')) {
    svgString = svgString.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
  }
  return svgString;
}

function downloadFile(content: Blob, filename: string): void {
  const link = document.createElement('a');
  const url = URL.createObjectURL(content);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function generateChartFilename(extension: 'png' | 'svg'): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${t().csv.chartFilenamePrefix}_${year}-${month}-${day}.${extension}`;
}

// ─────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────

/**
 * Exports the chart as a standalone SVG file (fonts embedded).
 */
export async function exportChartAsSvg(
  svgElement: SVGElement,
  options: ChartExportOptions = {}
): Promise<void> {
  const { svg } = await prepareExportSvg(svgElement, options);
  const blob = new Blob([serializeSvg(svg)], { type: 'image/svg+xml;charset=utf-8' });
  downloadFile(blob, options.filename || generateChartFilename('svg'));
}

/**
 * Exports the chart as a PNG at 2x resolution (engine device_scale).
 */
export async function exportChartAsPng(
  svgElement: SVGElement,
  options: ChartExportOptions = {},
  scale: number = 2
): Promise<void> {
  const { svg, width, height } = await prepareExportSvg(svgElement, options, true);
  const svgString = serializeSvg(svg);

  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    console.error('Failed to get canvas context');
    return;
  }
  ctx.scale(scale, scale);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);

  const img = new Image();
  const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);

  img.onload = () => {
    ctx.drawImage(img, 0, 0, width, height);
    URL.revokeObjectURL(url);
    canvas.toBlob(blob => {
      if (blob) {
        downloadFile(blob, options.filename || generateChartFilename('png'));
      }
    }, 'image/png');
  };
  img.onerror = () => {
    console.error('Failed to load SVG image for PNG export');
    URL.revokeObjectURL(url);
  };
  img.src = url;
}
